import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Moment from 'moment';
import {
  AppDatePicker,
  AppCheckbox,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import minions from '../../../../../minions/minions';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';

export default function PrevisionEditForm({
  resourceObligationIndex,
  resourceObligationID,
  selectedPrevision,
  isLoading,
  onCancel,
  onSave,
  elementID,
}) {
  const { moduleBridgeHex, page } = useParams();
  const dispatch = useDispatch();
  const view = useSelector((state) => state.currentPage?.view);
  const resLevel1 = useSelector(
    (state) => state.currentPage?.resourceObligationLevels?.level1
  );
  const resourceObligationsIDs = useSelector(
    (s) => s.currentPage?.resourceObligationsPage?.[page - 1]
  );
  const [validationSchema] = useState(
    Yup.object().shape({
      startDate: Yup.string().required(
        ReactDOMServer.renderToString(
          <Translator file="user" name="user.required" />
        )
      ),
      endDate: Yup.string()
        .required(
          ReactDOMServer.renderToString(
            <Translator file="user" name="user.required" />
          )
        )
        .nullable(),
    })
  );
  const [error, setError] = useState();

  const handleCancel = () => {
    if (isLoading) return;
    onCancel();
  };

  const handleUpdatePrevision = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_prevision_update"
          ),
          minions.forms.toFormData({
            ...values,
            moduleBridgeHex,
            roIDs: resourceObligationsIDs,
            previsionID: selectedPrevision?.id,
            view,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then(() => {
          setError(undefined);
          dispatch(currentPageAction.change("updateCard", elementID, true));
          dispatch(
            currentPageAction.change(
              "updateResourceObligationTab",
              elementID,
              true
            )
          );
          onSave();
        })
        .catch((e) => {
          if (e.response.status === 406) setError(e.response.data);
        }),
    [
      moduleBridgeHex,
      selectedPrevision,
      resourceObligationIndex,
      resourceObligationID,
      resLevel1,
      resourceObligationsIDs,
      view,
    ]
  );

  return (
    <PrevisionForm>
      <Formik
        initialValues={{
          startDate: selectedPrevision?.startDate || '',
          endDate: selectedPrevision?.endDate || '',
          autoEndDate: Moment(
            selectedPrevision?.startDate,
            'DD/MM/YYYY'
          ).isAfter(Moment().startOf('day')),
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleUpdatePrevision}
      >
        {({ handleSubmit, values, setFieldValue, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className="container-fluid">
              <h3>
                <Translator
                  file="module-resourceManager"
                  name="resource.period.from.startDate.endDate"
                  variables={{
                    starDate: selectedPrevision?.startDate,
                    endDate: selectedPrevision?.endDate,
                  }}
                />
              </h3>
              <div className="row">
                <div className="col-6">
                  <AppDatePicker
                    title={
                      <Translator
                        file="module-resourceManager"
                        name="resource.start.period"
                      />
                    }
                    value={values.startDate}
                    onChange={(opt) => setFieldValue('startDate', opt)}
                    error={errors.startDate}
                    hasToggleType
                  />
                </div>
                <div className="col-6">
                  {/* {if endDate  > now} */}
                  {Moment(selectedPrevision?.startDate, 'DD/MM/YYYY').isAfter(
                    Moment().startOf('day')
                  ) ? (
                    <>
                      <AppDatePicker
                        title={
                          <Translator
                            file="module-resourceManager"
                            name="resource.end.period"
                          />
                        }
                        value={values.endDate}
                        onChange={(opt) => setFieldValue('endDate', opt)}
                        error={errors.endDate}
                        isDisabled={values.autoEndDate}
                        hasToggleType
                      />
                      <AppCheckbox
                        value={values.autoEndDate}
                        onChange={(opt) => setFieldValue('autoEndDate', opt)}
                        hasBorder
                        hasToggleType
                      >
                        <Translator
                          file="module-resourceManager"
                          name="resource.auto.adjust.end.date.period"
                        />
                      </AppCheckbox>
                    </>
                  ) : (
                    <AppDatePicker
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.end.period"
                        />
                      }
                      value={values.endDate}
                      onChange={(opt) => setFieldValue('endDate', opt)}
                      error={errors.endDate}
                      hasToggleType
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="alert alert-info">
                    <Translator
                      file="module-resourceManager"
                      name="resource.new.dates.cannot.completely.overlap.periods"
                    />
                  </div>
                  {error && (
                    <div className="alert alert-danger mt-2 mb-2">{error}</div>
                  )}
                  <span
                    className={`btn btn-xs btn-outline-light mr-2 ${
                      isLoading ? 'disabled' : ''
                    }`}
                    onClick={handleCancel}
                  >
                    <Translator file="user" name="user.cancel" />
                  </span>
                  <button
                    type="submit"
                    className={`btn btn-xs btn-outline-light float-right ${
                      isLoading ? 'disabled' : ''
                    }`}
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.update"
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </PrevisionForm>
  );
}

const PrevisionForm = styled.div`
  h3 {
    font-size: 13px;
    font-weight: bold;
  }
`;
